/* Nanak Operations — Drivers & Roster + Time-Correction review (owner only, Phase C/D).
   Local-only driver management; never writes to Limo Anywhere. In development no real
   invitation emails are sent, so the single-use invite link is shown for the owner to copy. */
import { useCallback, useEffect, useState } from "react";
import { UserPlus, Loader2, Copy, Check, RefreshCw, Ban, ShieldCheck, Mail } from "lucide-react";
import { api } from "@/lib/auth";
import { Panel, CardLabel, Eyebrow, StatusChip, fmtDate } from "@/components/portal/opsui";
import { Users, ListChecks } from "lucide-react";

const DRIVER_TONE = { active: "active", invited: "upcoming", revoked: "canceled", suspended: "canceled" };

export function DriversRoster() {
  const [drivers, setDrivers] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", employment_type: "employee" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [invite, setInvite] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    api.get("/ops/drivers").then((r) => setDrivers(r.data.drivers)).catch(() => setDrivers([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault(); setBusy(true); setErr(""); setInvite(null);
    try {
      const { data } = await api.post("/ops/drivers", form);
      setInvite({ link: data.invite_link, expires: data.invite_expires_at });
      setForm({ first_name: "", last_name: "", email: "", employment_type: "employee" });
      load();
    } catch (e2) {
      setErr(typeof e2.response?.data?.detail === "string" ? e2.response.data.detail : "Could not create driver");
    } finally { setBusy(false); }
  };

  const act = async (id, action) => {
    try { const { data } = await api.post(`/ops/drivers/${id}/${action}`, {});
      if (data.invite_link) { setInvite({ link: data.invite_link, expires: data.invite_expires_at }); }
      load();
    } catch { /* surfaced via reload */ }
  };
  const copy = (txt) => { navigator.clipboard?.writeText(txt); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls = "w-full rounded-lg border border-line bg-obsidian-900 px-3 py-2 text-sm text-ivory placeholder:text-dim-charcoal focus:border-gold/50 focus:outline-none";

  return (
    <div className="space-y-5 p-6" data-testid="ops-drivers-section">
      <div className="grid gap-5 lg:grid-cols-5">
        <Panel className="lg:col-span-2">
          <CardLabel icon={UserPlus}>Invite a Driver</CardLabel>
          <form onSubmit={create} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="driver-first-name" required placeholder="First name" value={form.first_name} onChange={set("first_name")} className={inputCls} />
              <input data-testid="driver-last-name" required placeholder="Last name" value={form.last_name} onChange={set("last_name")} className={inputCls} />
            </div>
            <input data-testid="driver-email" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} />
            <select data-testid="driver-employment" value={form.employment_type} onChange={set("employment_type")} className={inputCls}>
              <option value="employee">Employee (payroll trip-clock)</option>
              <option value="contractor">Contractor</option>
            </select>
            {err && <p data-testid="driver-invite-error" className="text-xs text-red-400">{err}</p>}
            <button data-testid="driver-invite-submit" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-widest text-obsidian-900 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send Invitation
            </button>
            <p className="text-[10px] text-dim-charcoal">Development mode: no real email is sent. Share the secure link below.</p>
          </form>
          {invite && (
            <div data-testid="driver-invite-link" className="mt-4 rounded-xl border border-gold/25 bg-obsidian-900 p-3">
              <p className="text-[10px] uppercase tracking-widest text-gold-dark">Single-use invite link · expires {fmtDate(invite.expires)}</p>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 truncate text-[11px] text-ivory">{invite.link}</code>
                <button onClick={() => copy(invite.link)} data-testid="driver-invite-copy" className="rounded-md border border-gold/30 p-1.5 text-gold-light hover:bg-gold/10">{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}</button>
              </div>
            </div>
          )}
        </Panel>

        <Panel className="lg:col-span-3">
          <CardLabel icon={Users} right={<span className="text-[11px] text-dim-charcoal">Local roster · not synced to Limo Anywhere</span>}>Driver Roster</CardLabel>
          {drivers === null ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-gold-light" /></div>
            : drivers.length === 0 ? <div className="py-12 text-center" data-testid="ops-drivers-empty"><Users className="mx-auto h-7 w-7 text-muted-steel" /><p className="mt-3 text-sm text-ivory">No drivers yet</p><p className="mt-1 text-xs text-muted-steel">Invite your first Nanak driver using the form.</p></div>
            : <div className="space-y-2">{drivers.map((d) => (
              <div key={d.driver_id} data-testid={`driver-row-${d.driver_id}`} className="flex items-center justify-between rounded-xl border border-line bg-obsidian-900 p-3">
                <div>
                  <p className="text-sm font-medium text-ivory">{d.first_name} {d.last_name} <span className="ml-1 text-[10px] uppercase tracking-wider text-muted-steel">{d.employment_type}</span></p>
                  <p className="text-xs text-muted-steel">{d.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusChip tone={DRIVER_TONE[d.status] || "upcoming"} label={d.status} />
                  {d.status !== "active" && d.status !== "revoked" && <button data-testid={`driver-resend-${d.driver_id}`} onClick={() => act(d.driver_id, "resend")} title="Resend invite" className="rounded-md border border-gold/30 p-1.5 text-gold-light hover:bg-gold/10"><RefreshCw className="h-3.5 w-3.5" /></button>}
                  {d.status === "invited" && <button data-testid={`driver-revoke-${d.driver_id}`} onClick={() => act(d.driver_id, "revoke")} title="Revoke invite" className="rounded-md border border-red-500/30 p-1.5 text-red-400 hover:bg-red-500/10"><Ban className="h-3.5 w-3.5" /></button>}
                  {d.status === "active" && <button data-testid={`driver-suspend-${d.driver_id}`} onClick={() => act(d.driver_id, "suspend")} title="Suspend" className="rounded-md border border-red-500/30 p-1.5 text-red-400 hover:bg-red-500/10"><Ban className="h-3.5 w-3.5" /></button>}
                  {d.status === "suspended" && <button data-testid={`driver-reactivate-${d.driver_id}`} onClick={() => act(d.driver_id, "reactivate")} title="Reactivate" className="rounded-md border border-gold/30 p-1.5 text-gold-light hover:bg-gold/10"><ShieldCheck className="h-3.5 w-3.5" /></button>}
                </div>
              </div>))}</div>}
        </Panel>
      </div>
    </div>
  );
}

export function CorrectionsReview() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    api.get("/ops/corrections").then((r) => setItems(r.data.corrections)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    const note = window.prompt(`Optional note for this ${action}`) || "";
    try { await api.post(`/ops/corrections/${id}/${action}`, { note }); load(); }
    catch (e) { window.alert(e.response?.data?.detail || "Action failed"); }
  };

  return (
    <div className="p-6" data-testid="ops-corrections-section">
      <Panel>
        <CardLabel icon={ListChecks} right={<span className="text-[11px] text-dim-charcoal">Manager approval · original timestamp always preserved</span>}>Time-Correction Requests</CardLabel>
        {items === null ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-gold-light" /></div>
          : items.length === 0 ? <div className="py-12 text-center" data-testid="ops-corrections-empty"><ListChecks className="mx-auto h-7 w-7 text-muted-steel" /><p className="mt-3 text-sm text-ivory">No time-correction requests</p></div>
          : <div className="space-y-2">{items.map((c) => (
            <div key={c.correction_id} data-testid={`correction-row-${c.correction_id}`} className="rounded-xl border border-line bg-obsidian-900 p-4">
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium text-ivory">{c.driver_name || "Driver"} · #{c.confirmation_number || c.reservation_id}</p>
                  <p className="mt-0.5 text-xs text-muted-steel">{c.field}: <span className="line-through">{fmtDate(c.original) || c.original || "—"}</span> → <span className="text-gold-light">{fmtDate(c.proposed) || c.proposed}</span></p>
                  <p className="mt-1 text-xs text-ivory/70">“{c.reason}”</p></div>
                <StatusChip tone={c.status === "approved" ? "active" : c.status === "rejected" ? "canceled" : "review"} label={c.status === "revision_requested" ? "revision requested" : c.status} />
              </div>
              {c.decision_note && c.status !== "pending" && <p className="mt-2 text-xs text-amber-300">Note: {c.decision_note}</p>}
              {c.status === "pending" && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button data-testid={`correction-approve-${c.correction_id}`} onClick={() => decide(c.correction_id, "approve")} className="rounded-full border border-gold/40 px-4 py-1.5 text-xs font-semibold text-gold-light hover:bg-gold/10">Approve</button>
                  <button data-testid={`correction-revise-${c.correction_id}`} onClick={() => decide(c.correction_id, "revise")} className="rounded-full border border-amber-500/40 px-4 py-1.5 text-xs font-semibold text-amber-300 hover:bg-amber-500/10">Request revision</button>
                  <button data-testid={`correction-reject-${c.correction_id}`} onClick={() => decide(c.correction_id, "reject")} className="rounded-full border border-red-500/30 px-4 py-1.5 text-xs font-semibold text-red-400 hover:bg-red-500/10">Reject</button>
                </div>
              )}
            </div>))}</div>}
      </Panel>
    </div>
  );
}
